import { Alert, Spinner } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { C2_EDDY_LOAD_TASK_ID, readC2EddyLoadRunResult, useRefreshC2EddyLoadResults } from '@/entities/integrations';
import { isRunActive, useRun } from '@/entities/jobs';

const RUN_POLL_INTERVAL_MS = 3000;

type Props = {
  runId: string;
};

/**
 * Ход прогона загрузки периода: статус, пока прогон идёт, а по завершении — итог: сколько
 * коммуникаций создано и обновлено, по скольким обращениям загрузка не удалась и сколько
 * картинок получить не удалось.
 */
export const C2EddyLoadRun: FC<Props> = ({ runId }) => {
  const { t } = useTranslation(['integrations', 'runs']);
  const refreshLoadResults = useRefreshC2EddyLoadResults();
  const { data: run, isError } = useRun(runId, {
    retry: 0,
    // Ответа ещё нет — опрос тоже идёт: прогон только что поставлен в очередь
    refetchInterval: (current) => (!current || isRunActive(current.status) ? RUN_POLL_INTERVAL_MS : false),
  });

  const isFinished = run !== undefined && !isRunActive(run.status);

  // Пока прогон шёл, он наполнял журнал и отмечал обращения превью загруженными
  useEffect(() => {
    if (isFinished) refreshLoadResults();
  }, [isFinished, refreshLoadResults]);

  if (!run) {
    if (isError) return <Alert type="error" title={t('integrations:c2Eddy.run.unknown')} />;
    return <Spinner className="size-6" />;
  }

  const statusTitle = t('integrations:c2Eddy.run.status', { status: t(`runs:status.${run.status}`) });
  const loadTask = run.tasks.find((task) => task.taskId === C2_EDDY_LOAD_TASK_ID);

  if (isRunActive(run.status)) {
    return (
      <Alert
        type="info"
        title={statusTitle}
        description={t('integrations:c2Eddy.run.progress', { percent: loadTask?.progress ?? 0 })}
      />
    );
  }

  if (run.status === 'FAILED') {
    return <Alert type="error" title={statusTitle} description={run.error?.message ?? loadTask?.error?.message} />;
  }

  if (run.status === 'CANCELLED') {
    return <Alert type="warning" title={statusTitle} />;
  }

  const result = readC2EddyLoadRunResult(loadTask?.results);

  return (
    <Alert
      type={result && result.errors > 0 ? 'warning' : 'success'}
      title={statusTitle}
      description={result && t('integrations:c2Eddy.run.result', result)}
    />
  );
};
